import $ from 'jquery';
import './jquery.zoomable';

import '../css/PageViewer.scss';
import PageFlipper from './PageFlipper';

export default class PageViewer {

  constructor( settings ) {

    if ( typeof settings !== 'object' || !settings )
      throw 'You must provide a settings object';

    // We check for all required functions up-front so that none are missing for sure
    var required = [
      'containerId',
      'pageClass',
      'numberOfPages',
      'requestPageHtmlFunc',
      'cancelPageHtmlRequestFunc',
      'pageIsZoomableFunc',
      'indexChangedFunc',
      'pageShownFunc',
      'pageFinishLoadFunc',
      'pagePreRemoveFunc',
      'pageRemovedFunc',
    ];

    for ( var i = 0; i < required.length; i++ ) {
      if ( !settings[ required[ i ] ] ) {
        throw 'Setting \''+ required[ i ] +'\' is required';
      }
    }

    this.requests = [ null, null, null ];
    this.requestProcessTimeout = null;

    this.containerId = settings.containerId;
    this.pageClass = settings.pageClass;

    this.numberOfPages = settings.numberOfPages;
    this.maxColumns = settings.maxColumns || 2;
    this.minColumnWidth = settings.minColumnWidth || 500;
    this.currentIndex = settings.initialIndex || 0;

    this.currColumns = this.getColumnCount();

    // Function that is called when a page swipe happens, but before any HTML
    // has necessarily been loaded
    this.indexChangedFunc = settings.indexChangedFunc;
    // Function that is only called if the page is currently shown to the user.
    // Note that that page might be added to the DOM already, but pageShownFunc
    // is not called, since PageViewer keeps 3 pages visible in the DOM.
    this.pageShownFunc = settings.pageShownFunc;
    this.requestPageHtmlFunc = settings.requestPageHtmlFunc;
    this.cancelPageHtmlRequestFunc = settings.cancelPageHtmlRequestFunc;
    this.pageIsZoomableFunc = settings.pageIsZoomableFunc;
    // Function that is called when all of the page HTML has been loaded. This
    // is called before pageShownFunc.
    // This is called even for pages that are not the current page (since
    // PageFlipper keeps 3 pages loaded in the DOM: previous page, current page
    // and next page).
    this.pageFinishLoadFunc = settings.pageFinishLoadFunc;
    // Called before the page wrapper element is removed from the DOM
    this.pagePreRemoveFunc = settings.pagePreRemoveFunc;
    // Called after the page wrapper element is removed from the DOM
    this.pageRemovedFunc = settings.pageRemovedFunc;
    this.pageLoadErrorFunc = settings.pageLoadErrorFunc;

    this.pageFlipper = new PageFlipper( "#"+ this.containerId, {
      loop: false,
      numberOfPages: this.getPageFlipperPageCount(),
      useSwipeEvents: !settings.disablePageFlip,
      useMouseEvents: !settings.disableMousePageFlip,
    });

    this.resizeTimeout = null;
    $( window ).off( 'resize.pageviewer' );
    $( window ).on( 'resize.pageviewer', this.onResize.bind( this ) );

    this.pageFlipper.onFlip( this.onFlip.bind( this ) );

    this.pageFlipper.setCurrentPage( this.getSwipeviewPageForSubPage( this.currentIndex ) );
    this.init( this.currentIndex );
  }

  addRequest( swipeviewPage, slot ) {

    if ( swipeviewPage === undefined || swipeviewPage < 0 )
      return;

    if ( this.requests[ slot ] ) {
      this.cancelRequest( slot );
    }

    this.requests[ slot ] = {
      swipeviewPage: swipeviewPage,
      slot: slot,
      promiseBySubPage: {},
      started: false
    };
  }

  cancelRequest( slot ) {

    if ( !this.requests[ slot ] )
      return;

    var promises = this.requests[ slot ].promiseBySubPage;

    for ( var subPageIndex in promises ) {
      if ( promises.hasOwnProperty( subPageIndex ) ) {
        this.cancelPageHtmlRequestFunc( Number( subPageIndex ) );
      }
    }
  }

  processRequest() {

    var request = null;

    // Find a pending request with the smallest page number
    for (var i = 0; i < this.requests.length; i++) {
      if ( this.requests[i] && !this.requests[i].started ) {
        if ( !request || this.requests[i].swipeviewPage < request.swipeviewPage ) {
          request = this.requests[i];
        }
      }
    }

    if ( !request || this.destroyed ) {
      clearTimeout( this.requestProcessTimeout );
      this.requestProcessTimeout = null;
      return;
    }

    request.started = true;

    const onRequestSuccess = ( subPageIndex, html ) => {
      this.showPageForHtml( subPageIndex, html );
    };

    const onRequestFail = ( subPageIndex, error ) => {
      if ( window.console ) {
        window.console.error( error );
      }

      if ( this.destroyed )
        return;

      if ( this.pageLoadErrorFunc )
        this.pageLoadErrorFunc( error, subPageIndex );
    };

    const addSubPageRequest = ( subPageIndex ) => {
      this.requests[ request.slot ].promiseBySubPage[ subPageIndex ] =
        this.requestPageHtmlFunc( subPageIndex ).
          then( onRequestSuccess.bind( this, subPageIndex ) ).
          catch( onRequestFail.bind( this, subPageIndex ) );
    };

    var subPageStartIndex = this.getSubPageStartIndex( request.swipeviewPage );
    var subPagesToLoad = this.getSubPageCount( request.swipeviewPage );

    for ( var i = 0; i < subPagesToLoad; i++ ) {
      addSubPageRequest( subPageStartIndex + i );
    }

    // Start the next Swipeview page's request, if there's any non-started
    // requests left
    this.requestProcessTimeout = setTimeout( this.processRequest.bind( this ), 100 );
  }

  getSubPageStartIndex( swipeviewPage ) {
    return ( swipeviewPage != 0 ) 
      ? this.currColumns * ( swipeviewPage - 1 ) + 1
      : 0;
  }

  getSubPageCount( swipeviewPage ) {

    var startIndex = this.getSubPageStartIndex( swipeviewPage );
    var count = ( startIndex > 0 && startIndex < this.numberOfPages - 1 )
      ? this.currColumns 
      : 1;

    if ( startIndex + count > this.numberOfPages )
      count -= startIndex + count - this.numberOfPages;

    return count;
  }

  getColumnCount() {

    var container = $( '#'+ this.containerId );
    var containerWidth = container.width();
    var containerHeight = container.height();
    // We only show multiple columns when the container is in landscape
    // orientation (i.e. wider than taller)
    var columns = ( containerWidth >= containerHeight )
      ? Math.floor( containerWidth / this.minColumnWidth )
      : 1;

    if ( columns < 1 )
      columns = 1;
    else if ( columns > this.maxColumns )
      columns = this.maxColumns;

    return columns;
  }

  /**
   * Cancel all ajax requests and reload the pages, if the column count has changed
   */
  resetColumns() {

    if ( this.destroyed )
      return;

    var oldColCount = this.currColumns;
    var newColCount = this.getColumnCount();

    if ( newColCount === oldColCount )
      return;

    for ( var i = 0; i < this.requests.length; ++i ) {
      this.cancelRequest( i );
    }

    this.emptyAllPages();

    this.currColumns = newColCount;
    this.pageFlipper.updatePageCount( this.getPageFlipperPageCount() );
    this.pageFlipper.setCurrentPage( this.getSwipeviewPageForSubPage( this.currentIndex ) );
    this.currentIndex = this.getCurrentIndex();

    this.reloadPages();
  }

  reloadPages() {
    for ( var i = 0; i < this.requests.length; ++i ) {
      this.cancelRequest( i );
    }

    this.requests.length = 0;
    this.init( this.currentIndex );
  }

  emptyAllPages() {

    if ( this.destroyed )
      return;

    for ( var i = 0; i < this.getPageFlipperPageCount() + 1; i++ ) {

      if ( !this.pageFlipper.masterPages[ i ] )
        return;

      var pageIndex = parseInt( $( this.pageFlipper.masterPages[ i ] ).attr( 'data-page-index' ), 10 );
      var subPageStartIndex = this.getSubPageStartIndex( pageIndex );
      var subPageCount = this.getSubPageCount( pageIndex );

      // We must call pagePreRemoveFunc and pageRemovedFunc for all sub-pages
      // so that they have a chance to unbind any event handlers etc.

      for ( var j = 0; j < subPageCount; ++j ) {
        if ( $( '#asmag-swipeview-subpage-'+ ( subPageStartIndex + j ) ).length ) {
          this.pagePreRemoveFunc( subPageStartIndex + j );
        }
      }

      $( this.pageFlipper.masterPages[ i ] ).empty();

      for ( var j = 0; j < subPageCount; ++j ) {
        this.pageRemovedFunc( subPageStartIndex + j );
      }
    }
  }

  getPageFlipperPageCount() {
    return Math.ceil( ( this.numberOfPages - 1 ) / this.currColumns ) + 1;
  }

  getCurrentIndex() {
    return this.getSubPageStartIndex( this.pageFlipper.page );
  }

  getSwipeviewPageForSubPage( subPageIndex ) {
    return ( subPageIndex != 0 )
      ? Math.floor( ( subPageIndex - 1 ) / this.currColumns ) + 1
      : 0;
  }

  setColumnWidths( swipeviewPage ) {

    var swipeviewPageEl = $( '#'+ this.pageClass +'-'+ swipeviewPage );
    if ( !swipeviewPageEl.length )
      return;

    var subPageElems = swipeviewPageEl.children();
    var colWidth = Math.floor( 100 / subPageElems.length );
    for ( var i = 0; i < subPageElems.length; ++i ) {
      subPageElems.eq( i ).css( 'width', colWidth +'%' );
      subPageElems.eq( i ).css( 'left', ( i * colWidth ) +'%' );
    }
  }

  allSubPagesAreZoomable( swipeviewPage ) {

    if ( this.destroyed )
      return false;

    var subPageStartIndex = this.getSubPageStartIndex( swipeviewPage );
    var subPages = this.getSubPageCount( swipeviewPage );

    var allZoomable = true;
    for ( var i = subPageStartIndex; i < subPageStartIndex + subPages; i++ ) {
      if ( !this.pageIsZoomableFunc( i ) ) {
        allZoomable = false;
        break;
      }
    }

    return allZoomable;
  }

  onResize() {
    clearTimeout( this.resizeTimeout );
    this.resizeTimeout = setTimeout( this.resetColumns.bind( this ), 300 );
  }

  showPageForHtml( subPageIndex, html ) {

    var swipeviewPage = this.getSwipeviewPageForSubPage( subPageIndex );

    var swipeviewPageEl = $( '#'+ this.pageClass +'-'+ swipeviewPage );
    if ( !swipeviewPageEl.length ) {
      window.console && window.console.warn( 'Swipeview page element is missing' );
      return;
    }

    var subPageEl = swipeviewPageEl.find( '#asmag-swipeview-subpage-'+ subPageIndex );
    if ( !subPageEl.length ) {
      window.console && window.console.warn( 'Swipeview sub-page element is missing' );
      return;
    }

    // Remove the loading icon whenever at least one subpage has been loaded
    swipeviewPageEl.removeClass( 'inLoad' );

    // Note: we don't use jQuery's html() method here, since we don't want any
    // possible <script> elements to be evaluated. innerHTML never evaluates
    // <script> contents in the `html` variable. We do this so that any
    // page-related JavaScript logic must always be handled via the PageViewer
    // callback functions (e.g. pageFinishLoadFunc).
    subPageEl[ 0 ].innerHTML = html;

    if ( this.pageFinishLoadFunc ) {
      this.pageFinishLoadFunc( subPageIndex );
    }

    var currentPage = ( this.currentIndex != 0 )
      ? Math.floor( ( this.currentIndex - 1 ) / this.currColumns ) + 1
      : 0;

    if ( swipeviewPage == currentPage ) {
      if ( this.pageShownFunc ) {
        this.pageShownFunc( subPageIndex );
      }
    }

    this.updateZoomablePages( swipeviewPage );
  }

  init( shownSubPageIndex ) {

    const swipeviewPageCount = this.getPageFlipperPageCount();
    const startSwipeviewPage = ( swipeviewPageCount < 3 )
      ? 1
      : 0;
    const loadedSwipeviewPageCount = ( swipeviewPageCount < 3 )
      ? swipeviewPageCount + 1
      : 3;
    const shownSwipeviewPage = this.getSwipeviewPageForSubPage( shownSubPageIndex );

    for ( var i = startSwipeviewPage; i < loadedSwipeviewPageCount; i++ ) {

      // Current Swipeview page
      if ( i == this.pageFlipper.currentMasterPage ) {
        var swipeviewPage = shownSwipeviewPage;
      }
      // Prev Swipeview page
      else if ( ( this.pageFlipper.currentMasterPage > 0 && i == this.pageFlipper.currentMasterPage - 1 )
        || ( this.pageFlipper.currentMasterPage == 0 && i == 2 ) ) 
      {
        var swipeviewPage = shownSwipeviewPage - 1;

        if ( swipeviewPage < 0 )
          swipeviewPage = swipeviewPageCount - 1;
      }
      // Next Swipeview page
      else {
        var swipeviewPage = shownSwipeviewPage + 1;

        if ( swipeviewPage > swipeviewPageCount - 1 )
          swipeviewPage = 0;
      }

      var subPageStartIndex = this.getSubPageStartIndex( swipeviewPage );
      var subPagesToLoad = this.getSubPageCount( swipeviewPage );

      // Allow outside code to clean up the old subpage before it's removed
      for ( var j = 0; j < subPagesToLoad; ++j ) {
        if ( $( '#asmag-swipeview-subpage-'+ ( subPageStartIndex + j ) ).length ) {
          this.pagePreRemoveFunc( subPageStartIndex + j );
        }
      }

      $( this.pageFlipper.masterPages[i] ).empty();

      var pageEl = document.createElement( 'div' );
      pageEl.className = this.pageClass +' inLoad';
      pageEl.id = this.pageClass +'-'+ swipeviewPage;
      this.pageFlipper.masterPages[i].appendChild( pageEl );

      for ( var j = 0; j < subPagesToLoad; ++j ) {
        var subPageEl = document.createElement( 'div' );
        subPageEl.id = 'asmag-swipeview-subpage-'+ ( subPageStartIndex + j );
        subPageEl.className = 'asmag-swipeview-subpage';
        pageEl.appendChild( subPageEl );
      }

      this.setColumnWidths( swipeviewPage );

      for ( var j = 0; j < subPagesToLoad; ++j ) {
        this.pageRemovedFunc( subPageStartIndex + j );
      }

      this.addRequest( swipeviewPage, i );
    }

    this.processRequest();
  }

  onFlip() {

    var oldIndex = this.currentIndex;
    this.currentIndex = this.getCurrentIndex();

    if ( oldIndex != this.currentIndex ) {
      this.resetZoomOfAllPages();

      if ( this.indexChangedFunc ) {
        this.indexChangedFunc( oldIndex, this.currentIndex );
      }
    }

    const startSwipeviewPage = ( this.getPageFlipperPageCount() < 3 )
      ? 1
      : 0;
    const swipeviewPageCount = ( this.getPageFlipperPageCount() < 3 )
      ? this.getPageFlipperPageCount() + 1
      : 3;

    for ( var i = startSwipeviewPage; i < swipeviewPageCount; i++ ) {

      var nextSwipeviewPage = Number( this.pageFlipper.masterPages[i].dataset.upcomingPageIndex );
      var oldSwipeviewPage = Number( this.pageFlipper.masterPages[i].dataset.pageIndex );

      var pageEl = this.pageFlipper.masterPages[i].children[0];
      var $pageEl = $( pageEl );

      var subPageStartIndex = this.getSubPageStartIndex( nextSwipeviewPage );
      var subPagesToLoad = this.getSubPageCount( nextSwipeviewPage );

      // A page that we didn't swipe to
      if ( nextSwipeviewPage != oldSwipeviewPage ) {

        // Allow outside code to clean up the old subpage before it's removed
        var oldSubPageStartIndex = this.getSubPageStartIndex( oldSwipeviewPage );
        var oldSubPages = this.getSubPageCount( oldSwipeviewPage );
        for ( var j = 0; j < oldSubPages; ++j ) {
          this.pagePreRemoveFunc( oldSubPageStartIndex + j );
        }

        // Destroy the replaced page's previous jquery.zoomable instances
        // (and their event handlers) before emptying the wrapper
        for ( var j = 0; j < pageEl.children.length; ++j ) {
          $( pageEl.children[ i ] ).zoomable( 'destroy' );
        }

        // Empty the wrapper
        $pageEl.empty();
        pageEl.className = this.pageClass +' inLoad';
        pageEl.id = this.pageClass +'-'+ nextSwipeviewPage;

        for ( var j = 0; j < subPagesToLoad; ++j ) {
          var subPageEl = document.createElement( 'div' );
          subPageEl.id = 'asmag-swipeview-subpage-'+ ( subPageStartIndex + j );
          subPageEl.className = 'asmag-swipeview-subpage';
          pageEl.appendChild( subPageEl );
        }

        this.setColumnWidths( nextSwipeviewPage );

        for ( var j = 0; j < subPagesToLoad; ++j ) {
          this.pageRemovedFunc( subPageStartIndex + j );
        }

        this.addRequest( nextSwipeviewPage, i );
      }
      // The page we swiped to
      else if ( nextSwipeviewPage == this.pageFlipper.page ) {

        // If the page has already loaded before we finish the swipe, 
        // immediately run the handlers
        if ( $( '#'+ this.pageClass +'-'+ this.pageFlipper.page ).length > 0 ) {

          var subPages = this.getSubPageCount( this.pageFlipper.page );

          for ( var j = 0; j < subPages; ++j ) {
            if ( this.pageShownFunc ) {
              this.pageShownFunc( subPageStartIndex + j );
            }
          }
        }
      }
    }

    this.processRequest();
  }

  updateZoomablePages( swipeviewPage ) {

    var $pageEl = $( '#'+ this.pageClass +'-'+ swipeviewPage );

    var subPageStartIndex = this.getSubPageStartIndex( swipeviewPage );
    var subPagesToLoad = this.getSubPageCount( swipeviewPage );

    var allSubPagesZoomable = this.allSubPagesAreZoomable( swipeviewPage );
    if ( allSubPagesZoomable )
      $pageEl.zoomable( { pageFlipper: this.pageFlipper } );
    else
      $pageEl.zoomable( 'destroy' );

    var $subPages = $pageEl.children( '.asmag-swipeview-subpage' );

    for ( var j = 0; j < subPagesToLoad; ++j ) {
      var $subPage = $subPages.eq( j );

      if ( !allSubPagesZoomable && this.pageIsZoomableFunc( subPageStartIndex + j ) ) {
        $subPage.zoomable( { pageFlipper: this.pageFlipper } );
        // If there are two sub-pages shown in $pageEl, but only one of them is
        // zoomable, then we want to make the zoomable sub-page have a higher
        // z-index than the non-zoomable sub-page, so that when the user zooms
        // in, the zoomed in page will overlap the non-zoomable sub-page.
        $subPage.css( { zIndex: 1 } );
      }
      else {
        $subPage.zoomable( 'destroy' );
        $subPage.css( { zIndex: '' } );
      }
    }
  }

  resetZoomOfAllPages() {
    var $pageEl = $( '.'+ this.pageClass );
    var $subPages = $pageEl.children( '.asmag-swipeview-subpage' );

    $pageEl.zoomable( 'reset' );

    for ( var i = 0; i < $subPages.length; ++i ) {
      $subPages.eq( i ).zoomable( 'reset' );
    }
  }

  destroy() {
    this.destroyed = true;

    for ( var i = 0; i < this.requests.length; ++i ) {
      this.cancelRequest( i );
    }

    // TODO: unbind any event handlers registered by constructor() and the PageFlipper
    // TODO: call $subPage.zoomable( 'destroy' ) for all pages
  }
}
